
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const app = createApp(App)

// 注册所有图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(router)
app.use(ElementPlus)

// 将ElMessage挂载到全局，供API拦截器使用
window.ElMessage = ElMessage

// 全局错误处理，防止第三方扩展（如加密货币钱包）的错误影响应用
const handleGlobalErrors = (event) => {
  const error = event.error || event.reason
  if (error && error.message) {
    // 检测ResizeObserver错误
    const isResizeObserverError = 
      error.message.includes('ResizeObserver loop completed') ||
      error.message.includes('ResizeObserver loop limit exceeded')
    
    // 检测MutationObserver错误
    const isMutationObserverError = 
      error.message.includes('Failed to execute \'observe\' on \'MutationObserver\'') ||
      error.message.includes('parameter 1 is not of type \'Node\'') ||
      error.message.includes('MutationObserver')
    
    // 检测常见的浏览器扩展错误
    const isExtensionError = 
      error.message.includes('EIP-6963') ||
      error.message.includes('ProviderDetail') ||
      error.message.includes('chrome-extension://') ||
      error.message.includes('moz-extension://') ||
      error.message.includes('webkit-extension://') ||
      error.stack?.includes('chrome-extension://') ||
      error.stack?.includes('moz-extension://') ||
      (event.filename && (
        event.filename.includes('chrome-extension://') ||
        event.filename.includes('moz-extension://')
      ))
    
    if (isResizeObserverError) {
      console.warn('检测到ResizeObserver警告，已自动忽略:', error.message)
      event.preventDefault?.()
      return true
    }
    
    if (isMutationObserverError) {
      // 静默忽略MutationObserver错误，通常由Element Plus等库在DOM未准备好时触发
      event.preventDefault?.()
      return true
    }
    
    if (isExtensionError) {
      console.warn('检测到浏览器扩展错误，已自动忽略:', error.message)
      event.preventDefault?.()
      return true
    }
  }
}

// 监听全局JavaScript错误
window.addEventListener('error', handleGlobalErrors, true)

// 监听Promise rejection错误
window.addEventListener('unhandledrejection', handleGlobalErrors, true)

// 专门处理ResizeObserver和MutationObserver错误 - 更强力的抑制方法
const originalConsoleError = console.error
console.error = function(...args) {
  // 检查是否是ResizeObserver错误
  const message = args.join(' ')
  if (message.includes('ResizeObserver loop completed') || 
      message.includes('ResizeObserver loop limit exceeded')) {
    // 静默忽略这些错误
    return
  }
  // 检查是否是MutationObserver错误
  if (message.includes('Failed to execute \'observe\' on \'MutationObserver\'') ||
      message.includes('parameter 1 is not of type \'Node\'')) {
    // 静默忽略这些错误
    return
  }
  // 其他错误正常输出
  originalConsoleError.apply(console, args)
}

// 覆盖ResizeObserver以防止循环错误
const originalResizeObserver = window.ResizeObserver
window.ResizeObserver = class extends originalResizeObserver {
  constructor(callback) {
    super((entries, observer) => {
      try {
        callback(entries, observer)
      } catch (error) {
        if (error.message && error.message.includes('ResizeObserver loop completed')) {
          // 静默忽略ResizeObserver循环错误
          return
        }
        throw error
      }
    })
  }
}

// 原有的错误处理器
const resizeObserverErrorHandler = (e) => {
  if (e.message && e.message.includes('ResizeObserver loop completed')) {
    e.stopImmediatePropagation()
    e.preventDefault()
    return false
  }
}

window.addEventListener('error', resizeObserverErrorHandler, true)

// 最强力的webpack-dev-server错误拦截
if (process.env.NODE_ENV === 'development') {
  // 拦截所有可能的webpack错误处理函数
  const interceptWebpackErrors = () => {
    // 方法1: 拦截handleError函数
    if (window.__webpack_dev_server_client_overlay_handleError) {
      const original = window.__webpack_dev_server_client_overlay_handleError
      window.__webpack_dev_server_client_overlay_handleError = function(error) {
        if (error && error.message && error.message.includes('ResizeObserver loop completed')) {
          console.log('webpack handleError 已拦截ResizeObserver错误')
          return
        }
        return original.call(this, error)
      }
    }
    
    // 方法2: 拦截全局webpack客户端
    if (window.__webpack_dev_server_client__) {
      const client = window.__webpack_dev_server_client__
      if (client.onMessage) {
        const originalOnMessage = client.onMessage
        client.onMessage = function(message) {
          if (message.type === 'error' && message.data && 
              message.data.includes && message.data.includes('ResizeObserver loop completed')) {
            console.log('webpack onMessage 已拦截ResizeObserver错误')
            return
          }
          return originalOnMessage.call(this, message)
        }
      }
    }
    
    // 方法3: 拦截webpack模块中的错误处理
    if (window.webpackHotUpdate) {
      const originalUpdate = window.webpackHotUpdate
      window.webpackHotUpdate = function() {
        try {
          return originalUpdate.apply(this, arguments)
        } catch (error) {
          if (error.message && error.message.includes('ResizeObserver loop completed')) {
            console.log('webpack热更新已拦截ResizeObserver错误')
            return
          }
          throw error
        }
      }
    }
  }
  
  // 立即尝试拦截
  interceptWebpackErrors()
  
  // 延迟再次尝试拦截（确保webpack客户端已加载）
  setTimeout(interceptWebpackErrors, 1000)
  setTimeout(interceptWebpackErrors, 3000)
}

// 为Vue应用添加错误处理
app.config.errorHandler = (err, vm, info) => {
  // 如果是ResizeObserver错误，静默处理
  if (err && err.message && 
      (err.message.includes('ResizeObserver loop completed') ||
       err.message.includes('ResizeObserver loop limit exceeded'))) {
    console.warn('Vue应用检测到ResizeObserver警告，已忽略:', err.message)
    return
  }
  
  // 如果是MutationObserver错误，静默处理
  if (err && err.message && 
      (err.message.includes('Failed to execute \'observe\' on \'MutationObserver\'') ||
       err.message.includes('parameter 1 is not of type \'Node\''))) {
    // 静默忽略MutationObserver错误
    return
  }
  
  // 如果是扩展相关错误，静默处理
  if (err && err.message && 
      (err.message.includes('EIP-6963') || 
       err.message.includes('ProviderDetail') ||
       err.message.includes('chrome-extension://'))) {
    console.warn('Vue应用检测到扩展错误，已忽略:', err.message)
    return
  }
  
  // 其他Vue错误正常处理
  console.error('Vue应用错误:', err, info)
}

app.mount('#app')

// 专门处理移动端Element-Plus下拉组件的ResizeObserver错误
if (process.env.NODE_ENV === 'development') {
  // 监听窗口大小变化，在移动端视图下抑制错误
  let isMobile = window.innerWidth <= 768
  
  const handleResize = () => {
    const wasMobile = isMobile
    isMobile = window.innerWidth <= 768
    
    // 如果从桌面端切换到移动端，或者在移动端，增强错误抑制
    if (isMobile || (!wasMobile && isMobile)) {
      console.log('移动端视图，启用ResizeObserver错误抑制')
      
      // 临时禁用所有ResizeObserver错误
      const originalAddEventListener = EventTarget.prototype.addEventListener
      EventTarget.prototype.addEventListener = function(type, listener, options) {
        if (type === 'error' && isMobile) {
          const wrappedListener = function(event) {
            if (event.error && event.error.message && 
                event.error.message.includes('ResizeObserver loop completed')) {
              console.warn('移动端ResizeObserver错误已被拦截')
              event.stopImmediatePropagation()
              event.preventDefault()
              return false
            }
            return listener.call(this, event)
          }
          return originalAddEventListener.call(this, type, wrappedListener, options)
        }
        return originalAddEventListener.call(this, type, listener, options)
      }
    }
  }
  
  window.addEventListener('resize', handleResize)
  handleResize() // 初始检查
}

// 开发环境下隐藏ResizeObserver错误覆盖层
if (process.env.NODE_ENV === 'development') {
  // 延迟执行，确保webpack-dev-server已加载
  setTimeout(() => {
    // 监听DOM变化，自动隐藏ResizeObserver错误
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // 检查是否是webpack错误覆盖层
              if (node.id && node.id.includes('webpack') || 
                  node.className && node.className.includes('webpack')) {
                // 检查错误内容
                const errorText = node.textContent || node.innerText || ''
                if (errorText.includes('ResizeObserver loop completed')) {
                  console.log('自动隐藏ResizeObserver错误覆盖层')
                  node.style.display = 'none'
                  // 或者直接移除
                  setTimeout(() => node.remove(), 100)
                }
              }
            }
          })
        }
      })
    })
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  }, 1000)
} 